import { Component, ElementRef, Inject, OnInit, ViewChild, signal } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppValidators } from 'src/app/common/appValidators';
import { PolicyModel } from 'src/app/models/api/governance/policy.model';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import * as _ from 'lodash';
import { AccountSettingsHttpService } from 'src/app/services/httpServices/governance/accountSettingsHttpService/account-settings-http.service';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { BulkDomainImportResponse } from 'src/app/models/api/governance/bulk-domain-import.model';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';

@Component({
  selector: 'app-add-domain-dialog',
  templateUrl: './add-domain-dialog.component.html',
  styleUrls: ['./add-domain-dialog.component.scss'],
})
export class AddDomainDialogComponent implements OnInit {
  @ViewChild('csvFileInput') csvFileInput!: ElementRef<HTMLInputElement>;

  public waitingForResponse = false;
  public addDomainsFormGroup: UntypedFormGroup;
  public processingFile = signal(false);
  public skippedDomains = signal<string[]>([]);
  public invalidDomains = signal<string[]>([]);
  public domainsToReplace = signal<string[]>([]);
  public showSkippedDomains = signal(false);
  public showInvalidDomains = signal(false);
  public showDomainsToReplace = signal(false);
  private readonly _maxDomainsToAdd = 500;

  constructor(
    public dialogRef: MatDialogRef<AddDomainDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public violationData: PolicyModel,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _accountSettingsHttpService: AccountSettingsHttpService,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService,
    private readonly _logger: LoggerService
  ) {}

  public get domainsToAdd(): UntypedFormArray {
    return this.addDomainsFormGroup.get('domainsToAdd') as UntypedFormArray;
  }

  ngOnInit(): void {
    this.addDomainsFormGroup = this._formBuilder.group({
      domainsToAdd: this._formBuilder.array([
        this._formBuilder.group({
          domain: this._formBuilder.control('', [
            AppValidators.domain,
            Validators.required,
            this.createDuplicateValidator(),
          ]),
        }),
      ]),
    });
  }

  private createDuplicateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const domain = control.value.toLowerCase();
      const existingDomains = (this.violationData?.config?.allowedDomains || []).map((d) => d.toLowerCase());

      if (existingDomains.includes(domain)) {
        const currentSkipped = this.skippedDomains();
        if (!currentSkipped.includes(domain)) {
          this.skippedDomains.set([...currentSkipped, domain]);
        }
        return { duplicate: { value: domain } };
      }

      const isWildcard = domain.startsWith('*.');
      const formDomains = this.getOtherFormDomains(control);
      const allDomains = [...existingDomains, ...formDomains];

      if (isWildcard) {
        // When adding wildcard like *.some.test.com
        const baseDomain = domain.substring(2); // some.test.com

        // Block if this wildcard is covered by a broader wildcard
        // e.g., *.some.test.com is covered by *.test.com
        const coveringWildcard = this.findCoveringWildcard(baseDomain, allDomains);
        if (coveringWildcard) {
          return { coveredByWildcard: { existingDomain: coveringWildcard } };
        }

        // Block if a non-exact-base domain is in the form that conflicts
        // Allow test.com + *.test.com to coexist
        if (formDomains.some((d) => d !== baseDomain && d.endsWith(`.${baseDomain}`))) {
          const conflicting = formDomains.find((d) => d !== baseDomain && d.endsWith(`.${baseDomain}`));
          return { wildcardConflictInForm: { existingDomain: conflicting } };
        }

        // Also track subdomains that would be covered by this wildcard
        const coveredDomains = existingDomains.filter((d) => !d.startsWith('*.') && d.endsWith(`.${baseDomain}`));
        coveredDomains.forEach((d) => this.addDomainToReplace(d));
      } else {
        // When adding non-wildcard, check if covered by ANY existing wildcard
        const coveringWildcard = this.findCoveringWildcard(domain, allDomains);

        if (coveringWildcard) {
          return { coveredByWildcard: { existingDomain: coveringWildcard } };
        }
      }

      const currentSkipped = this.skippedDomains();
      this.skippedDomains.set(currentSkipped.filter((d) => d !== domain));

      return null;
    };
  }

  private addDomainToReplace(domain: string): void {
    const current = this.domainsToReplace();
    if (!current.includes(domain)) {
      this.domainsToReplace.set([...current, domain]);
    }
  }

  private removeDomainFromReplace(domain: string): void {
    const current = this.domainsToReplace();
    this.domainsToReplace.set(current.filter((d) => d !== domain));
  }

  private getOtherFormDomains(currentControl: AbstractControl): string[] {
    if (!this.domainsToAdd) {
      return [];
    }
    return this.domainsToAdd.controls
      .map((group) => group.get('domain'))
      .filter((ctrl) => ctrl && ctrl !== currentControl && ctrl.value)
      .map((ctrl) => (ctrl!.value as string).toLowerCase());
  }

  private findCoveringWildcard(domain: string, allDomains: string[]): string | null {
    // Find wildcards that cover this domain
    // e.g., *.test.com covers some.test.com, foo.bar.test.com
    const wildcards = allDomains.filter((d) => d.startsWith('*.'));

    for (const wildcard of wildcards) {
      const wildcardBase = wildcard.substring(2); // *.test.com -> test.com
      // Only block subdomain matches (some.test.com covered by *.test.com)
      // Allow exact base match (test.com + *.test.com can coexist)
      if (domain !== wildcardBase && domain.endsWith(`.${wildcardBase}`)) {
        return wildcard;
      }
    }

    return null;
  }

  public addMoreClicked() {
    const domains = this.domainsToAdd;
    if (domains.length < this._maxDomainsToAdd) {
      domains.push(
        this._formBuilder.group({
          domain: this._formBuilder.control('', [
            AppValidators.domain,
            Validators.required,
            this.createDuplicateValidator(),
          ]),
        })
      );
    } else {
      this._notificationService.warning(`Maximum ${this._maxDomainsToAdd} domains allowed`);
    }
  }

  public removeDomain(indexToRemove: number) {
    if (indexToRemove >= 0 && indexToRemove < this.domainsToAdd.length) {
      const domainControl = this.domainsToAdd.at(indexToRemove);
      const domainValue = domainControl.get('domain')?.value?.toLowerCase();

      this.domainsToAdd.removeAt(indexToRemove);

      if (domainValue) {
        const currentSkipped = this.skippedDomains();
        this.skippedDomains.set(currentSkipped.filter((d) => d.toLowerCase() !== domainValue));
      }
    }
  }

  public addDomains() {
    // Get all domain values first
    const domainsList: string[] = _.map(this.addDomainsFormGroup.value.domainsToAdd, 'domain').filter(
      (domain) => domain && domain.trim()
    );

    // Check for wildcard/base domain conflicts within the form FIRST
    const conflicts = this.findInFormConflicts(domainsList);
    if (conflicts.length > 0) {
      this._notificationService.error(
        `Cannot add both "${conflicts[0].base}" and "${conflicts[0].wildcard}" - remove one first`
      );
      return;
    }

    if (this.addDomainsFormGroup.invalid) {
      // Mark all controls as touched to show validation errors
      this.domainsToAdd.controls.forEach((group) => {
        group.get('domain')?.markAsTouched();
      });
      return;
    }

    this.dialogRef.close({
      newDomains: domainsList,
      domainsToRemove: this.domainsToReplace(),
      skippedCount: this.skippedDomains().length,
      invalidCount: this.invalidDomains().length,
    });
  }

  private findInFormConflicts(domains: string[]): { base: string; wildcard: string }[] {
    const conflicts: { base: string; wildcard: string }[] = [];
    const lowerDomains = domains.map((d) => d.toLowerCase());
    const wildcards = lowerDomains.filter((d) => d.startsWith('*.'));

    for (const domain of lowerDomains) {
      if (!domain.startsWith('*.')) {
        // Check if this domain is covered by any wildcard in the form
        // Allow exact base match (test.com + *.test.com can coexist)
        for (const wildcard of wildcards) {
          const wildcardBase = wildcard.substring(2);
          // Only block subdomain match (some.test.com vs *.test.com)
          if (domain !== wildcardBase && domain.endsWith(`.${wildcardBase}`)) {
            conflicts.push({ base: domain, wildcard: wildcard });
            break;
          }
        }
      }
    }

    return conflicts;
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  public toggleSkippedDomains(): void {
    this.showSkippedDomains.set(!this.showSkippedDomains());
  }

  public toggleInvalidDomains(): void {
    this.showInvalidDomains.set(!this.showInvalidDomains());
  }

  public toggleDomainsToReplace(): void {
    this.showDomainsToReplace.set(!this.showDomainsToReplace());
  }

  public isBaseDomainMissing(wildcardValue: string): boolean {
    const baseDomain = wildcardValue.substring(2).toLowerCase();
    const existing = (this.violationData?.config?.allowedDomains || []).map((d) => d.toLowerCase());
    const formDomains = this.domainsToAdd.controls
      .map((group) => group.get('domain')?.value)
      .filter((v) => v && !v.startsWith('*.'))
      .map((v: string) => v.toLowerCase());
    return !existing.includes(baseDomain) && !formDomains.includes(baseDomain);
  }

  public triggerFileUpload(): void {
    this.csvFileInput.nativeElement.click();
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    if (!file.name.endsWith('.csv')) {
      this._notificationService.error('Please upload a CSV file');
      input.value = '';
      return;
    }

    this.processingFile.set(true);
    this.processCSVFile(file);
    input.value = '';
  }

  private processCSVFile(file: File): void {
    const integrationId = this.violationData.integrationId;
    const policyId = this.violationData.id;

    this._policiesHttpService.bulkImportDomains(integrationId, policyId, file).subscribe({
      next: (response: BulkDomainImportResponse) => {
        this.processingFile.set(false);

        if (!response.success) {
          this._notificationService.error(response.message || 'Failed to process CSV file');
          return;
        }

        this.skippedDomains.set(response.skipped_domains);
        this.invalidDomains.set(response.invalid_domains);

        if (response.new_domains.length === 0) {
          if (response.skipped_count > 0 || response.invalid_domains.length > 0) {
            this._notificationService.info(response.message);
          } else {
            this._notificationService.warning('No domains found in the CSV file');
          }
          return;
        }

        const addedCount = this.addNewDomainsToForm(response.new_domains);

        let message = `${addedCount} domain${addedCount > 1 ? 's' : ''} ready to add`;
        if (response.skipped_count > 0) {
          message += `, ${response.skipped_count} skipped`;
        }
        if (response.invalid_domains.length > 0) {
          message += `, ${response.invalid_domains.length} invalid`;
        }
        if (response.duplicates_removed > 0) {
          message += ` (${response.duplicates_removed} duplicate${response.duplicates_removed > 1 ? 's' : ''} removed from file)`;
        }

        this._notificationService.success(message);
      },
      error: (error) => {
        this.processingFile.set(false);
        this._logger.error('Error calling bulk import API', {}, error);
        this._httpErrorHandlerService.handle(error);
      },
    });
  }

  private addNewDomainsToForm(domains: string[]): number {
    // Get existing domains in the form
    const formDomains = this.domainsToAdd.controls
      .map((control) => control.get('domain')?.value?.toLowerCase())
      .filter((domain) => domain);

    const formDomainsSet = new Set(formDomains);

    // Filter out domains already in the form
    const domainsToAdd = domains.filter((domain) => !formDomainsSet.has(domain.toLowerCase()));

    if (domainsToAdd.length === 0) {
      return 0;
    }

    // Remove empty form controls before adding new ones
    for (let i = this.domainsToAdd.length - 1; i >= 0; i--) {
      const domainControl = this.domainsToAdd.at(i).get('domain');
      if (!domainControl?.value || domainControl.value.trim() === '') {
        this.domainsToAdd.removeAt(i);
      }
    }

    // Add new domains to the form
    let addedCount = 0;
    for (const domain of domainsToAdd) {
      if (this.domainsToAdd.length >= this._maxDomainsToAdd) {
        this._notificationService.warning(
          `Maximum ${this._maxDomainsToAdd} domains allowed. Only ${addedCount} domains were added.`
        );
        break;
      }

      this.domainsToAdd.push(
        this._formBuilder.group({
          domain: this._formBuilder.control(domain, [
            AppValidators.domain,
            Validators.required,
            this.createDuplicateValidator(),
          ]),
        })
      );
      addedCount++;
    }

    return addedCount;
  }
}
